import { useTable } from "@refinedev/core";
import { Link, useSearchParams } from "react-router";

import { Badge } from "@/components/Badge";
import {
  ErrorBox,
  FilterBar,
  Loading,
  PageHeader,
  Pagination,
  SearchInput,
  Select,
} from "@/components/Page";
import { EmptyRow, Table, Td, Th, Tr } from "@/components/Table";
import {
  SHIPMENT_KIND_OPTIONS,
  SHIPMENT_STATUS_OPTIONS,
  formatDateTime,
  shipmentKind,
  shipmentStatus,
} from "@/lib/format";
import { filterValue } from "@/lib/query";

interface ShipmentRow extends Record<string, unknown> {
  id: string;
  kind: string;
  status: string;
  tracking_number: string | null;
  target_point: string | null;
  created_at: string;
  delivered_at: string | null;
}

export function ShipmentsListPage() {
  const [searchParams] = useSearchParams();
  const statusZAdresu = searchParams.get("status") ?? "";

  const { result, tableQuery, filters, setFilters, currentPage, setCurrentPage, pageSize } =
    useTable<ShipmentRow>({
      resource: "shipments",
      sorters: { initial: [{ field: "created_at", order: "desc" }] },
      filters: {
        initial: statusZAdresu
          ? [{ field: "status", operator: "eq", value: statusZAdresu }]
          : undefined,
      },
    });

  const ustawFiltr = (field: string, value: string) =>
    setFilters([{ field, operator: "eq", value }], "merge");

  return (
    <>
      <PageHeader
        title="Przesyłki"
        description="Starter Boxy, książki i kopie. Tworzenie przesyłek i etykiety wchodzą razem z integracją ShipX - na razie ten ekran tylko pokazuje stan."
      />

      <FilterBar>
        <Select
          label="Status"
          value={filterValue(filters, "status")}
          options={SHIPMENT_STATUS_OPTIONS}
          onChange={(value) => ustawFiltr("status", value)}
        />
        <Select
          label="Rodzaj"
          value={filterValue(filters, "kind")}
          options={SHIPMENT_KIND_OPTIONS}
          onChange={(value) => ustawFiltr("kind", value)}
        />
        <SearchInput
          label="Szukaj"
          placeholder="numer śledzenia, kod paczkomatu"
          value={filterValue(filters, "query")}
          onChange={(value) => ustawFiltr("query", value)}
        />
      </FilterBar>

      <ErrorBox error={tableQuery.error} />

      {tableQuery.isLoading ? (
        <Loading />
      ) : (
        <>
          <Table>
            <thead>
              <tr>
                <Th>Utworzona</Th>
                <Th>Rodzaj</Th>
                <Th>Status</Th>
                <Th>Numer śledzenia</Th>
                <Th>Punkt</Th>
                <Th>Doręczona</Th>
              </tr>
            </thead>
            <tbody>
              {result.data.length === 0 && (
                <EmptyRow colSpan={6}>Żadna przesyłka nie pasuje do tych filtrów.</EmptyRow>
              )}
              {result.data.map((shipment) => {
                const status = shipmentStatus(shipment.status);
                return (
                  <Tr key={shipment.id}>
                    <Td nowrap>
                      <Link
                        to={`/przesylki/${shipment.id}`}
                        className="text-accent underline underline-offset-4 hover:text-accent-dark"
                      >
                        {formatDateTime(shipment.created_at)}
                      </Link>
                    </Td>
                    <Td nowrap>{shipmentKind(shipment.kind)}</Td>
                    <Td>
                      <Badge tone={status.tone}>{status.label}</Badge>
                    </Td>
                    <Td numeric>{shipment.tracking_number ?? "-"}</Td>
                    <Td>{shipment.target_point ?? "-"}</Td>
                    <Td nowrap>{formatDateTime(shipment.delivered_at)}</Td>
                  </Tr>
                );
              })}
            </tbody>
          </Table>

          <Pagination
            page={currentPage}
            pageSize={pageSize}
            total={result.total ?? 0}
            onChange={setCurrentPage}
          />
        </>
      )}
    </>
  );
}
